import type { MenuProps } from 'antd';
import { Button, Modal } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { Dropdown } from 'antd';
import { simulationServiceDeprecated } from '~/services';
import { DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import type { Simulation } from '~/store/simulation/types';
import { SimulationStatus } from '~/store/simulation/types';
import { resolveSimulationServiceVersion } from '~/store/simulation/resolveSimulationServiceVersion';
import { useStoreActions } from '~/store';
import { useSession } from '~/hooks/useSession';

const SimulationOptionsDropdown = ({ simulationSummaries }: { simulationSummaries: Simulation[] }) => {
  const cleanupSimulationAssets = useMutation({
    mutationFn: simulationServiceDeprecated.cleanupSimulationAssets,
  });

  const { isShipiumView } = useSession();

  const dispatchMessage = useStoreActions((actions) => actions.messages.dispatchMessage);

  const [modal, contextHolder] = Modal.useModal();

  const handleSimulationCleanup = async (): Promise<number> => {
    const simulationsToCleanup = simulationSummaries
      // v1 only: cleanup targets the v1 gateway; v2 (namespaced) asset cleanup is handled separately.
      .filter((simulation) => resolveSimulationServiceVersion(simulation.simulationSetupData) === 'v1')
      .filter((simulation) =>
        [
          SimulationStatus.COMPLETED,
          SimulationStatus.ARCHIVED,
          SimulationStatus.CANCELLED,
          SimulationStatus.EXCEPTION,
        ].includes(simulation.simulationStatus),
      )
      .map((simulation) => simulation.simulationId);

    let deletedAssetCount = 0;
    await Promise.all(
      simulationsToCleanup.map(async (simulationId) => {
        try {
          const response = await cleanupSimulationAssets.mutateAsync(simulationId);
          deletedAssetCount += response?.deletedAssets || 0;
        } catch (error) {
          console.error(`Error deleting simulation assets for ${simulationId}: ${error}`);
        }
      }),
    );
    return deletedAssetCount;
  };

  const menuItems: MenuProps['items'] = [];

  if (isShipiumView) {
    menuItems.push({
      key: 'cleanup-simulation-assets',
      label: (
        <>
          <Button
            style={{ margin: 0, padding: 0 }}
            onClick={() => {
              modal.confirm({
                title: 'Are you sure you want to delete all simulation assets?',
                onOk: async () => {
                  const deletedAssetCount = await handleSimulationCleanup();
                  dispatchMessage({
                    noticeType: 'success',
                    content: `Deleted ${deletedAssetCount.toLocaleString()} simulation assets`,
                  });
                },
              });
            }}
            type="link"
            danger
          >
            <DeleteOutlined /> Delete All Simulation Assets
          </Button>
          {contextHolder}
        </>
      ),
    });
  }

  return (
    <>
      {menuItems.length > 0 && (
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button icon={<MoreOutlined color={'#000'} />} />
        </Dropdown>
      )}
    </>
  );
};

export default SimulationOptionsDropdown;
